import { FormEvent, useEffect, useMemo, useState } from "react";
import { Clock3, FileText, Gauge, Plus, QrCode, Trash2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { CameraCapture } from "../components/CameraCapture";
import { SignaturePad } from "../components/SignaturePad";
import type { AnswerStatus, ChecklistItem, CompanyUnit, Inspection, Vehicle } from "../types";

type Answer = { status: AnswerStatus | ""; observation: string };

function formatBrt(value: string) {
  const normalized = value.includes("T") ? value : value.replace(" ", "T") + "Z";
  const date = new Date(normalized);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("pt-BR", {
        timeZone: "America/Fortaleza",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      }).format(date) + " BRT";
}

export function InspectionsPage() {
  const navigate = useNavigate();
  const { vehicleId: qrVehicleId } = useParams<{ vehicleId?: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qrMode = Boolean(qrVehicleId);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [units, setUnits] = useState<CompanyUnit[]>([]);
  const [unitFilter, setUnitFilter] = useState("");
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [vehicleId, setVehicleId] = useState("");
  const [meterValue, setMeterValue] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [signature, setSignature] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const [photoStorageReady, setPhotoStorageReady] = useState<boolean | null>(null);

  const selectedVehicle = useMemo(() => vehicles.find((v) => v.id === vehicleId), [vehicles, vehicleId]);
  const filteredInspections = useMemo(
    () => unitFilter ? inspections.filter((inspection) => inspection.unit_id === unitFilter) : inspections,
    [inspections, unitFilter]
  );
  const isForklift = (selectedVehicle?.type_name || "").toLowerCase().includes("empilhadeira");
  const meterType: "km" | "horimetro" = isForklift ? "horimetro" : "km";
  const meterRequired = Boolean(selectedVehicle && !isForklift);

  async function loadBase() {
    const [i, v, u] = await Promise.all([
      api<{ inspections: Inspection[] }>("inspections"),
      api<{ vehicles: Vehicle[] }>("vehicles"),
      api<{ units: CompanyUnit[] }>("units")
    ]);

    const activeVehicles = v.vehicles.filter((vehicle) => vehicle.active === 1);
    setInspections(i.inspections);
    setVehicles(activeVehicles);
    setUnits(u.units);

    if (qrVehicleId) {
      const target = activeVehicles.find((vehicle) => vehicle.id === qrVehicleId);
      if (target) {
        setVehicleId(target.id);
        setShowForm(true);
        setMessage("");
      } else {
        setVehicleId("");
        setShowForm(false);
        setMessage("Este QR Code pertence a um veículo que não está disponível na frota ativa. Procure um administrador.");
      }
      return;
    }

    if (!vehicleId && activeVehicles[0]) {
      setVehicleId(activeVehicles[0].id);
    }
  }

  useEffect(() => {
    loadBase();
    api<{ photo_storage_ready: boolean }>("system/status")
      .then((status) => setPhotoStorageReady(status.photo_storage_ready))
      .catch(() => setPhotoStorageReady(false));
  }, [qrVehicleId]);

  useEffect(() => {
    setMeterValue("");
    if (!selectedVehicle) return;
    api<{ items: ChecklistItem[] }>("checklists/" + selectedVehicle.vehicle_type_id).then((r) => {
      setItems(r.items);
      const initial: Record<string, Answer> = {};
      for (const item of r.items) initial[item.id] = { status: "", observation: "" };
      setAnswers(initial);
    });
  }, [selectedVehicle?.id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    if (!vehicleId || submitting) return;

    const pending = items.some((item) => !answers[item.id]?.status);
    if (pending) {
      setMessage("Responda todos os itens do checklist antes de finalizar.");
      return;
    }

    if (meterRequired && !meterValue) {
      setMessage("Informe a quilometragem atual do veículo.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        client_submission_id: submissionId,
        vehicle_id: vehicleId,
        meter_type: meterType,
        meter_value: meterValue || null,
        notes,
        answers: items.map((item) => ({ item_id: item.id, ...answers[item.id] })),
        photos,
        signature
      };

      const result = await api<{ inspection: Inspection; duplicate?: boolean }>("inspections", {
        method: "POST",
        body: JSON.stringify(payload)
      });

      setMeterValue("");
      setNotes("");
      setPhotos([]);
      setSignature(null);
      setSubmissionId(crypto.randomUUID());
      setShowForm(false);
      await loadBase();

      navigate("/inspecoes/" + result.inspection.id + "/relatorio");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível finalizar a inspeção.");
    } finally {
      setSubmitting(false);
    }
  }

  async function removeInspection(inspection: Inspection) {
    if (!isAdmin) return;

    const reason = window.prompt(
      "Motivo da exclusão desta inspeção:",
      "Inspeção de teste"
    );
    if (reason === null) return;

    const confirmed = window.confirm(
      "Excluir esta inspeção do histórico operacional e dos indicadores?\n\n" +
      "Ela deixará de contabilizar nos resultados, mas a ação ficará registrada na auditoria."
    );
    if (!confirmed) return;

    setMessage("");
    try {
      await api("inspections/" + inspection.id, {
        method: "DELETE",
        body: JSON.stringify({ reason: reason.trim() || "Inspeção de teste" })
      });
      setMessage("Inspeção excluída dos resultados e indicadores. A ação foi registrada na auditoria.");
      await loadBase();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Não foi possível excluir a inspeção.");
    }
  }


  return (
    <section>
      <div className="page-title">
        <div>
          <h1>Inspeções</h1>
          <p>Checklist operacional com KM/horímetro, fotos, assinatura, horário de servidor e relatório final.</p>
        </div>
        <button className="primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={17} /> Nova inspeção
        </button>
      </div>

      {message && <div className="alert error">{message}</div>}

      {qrMode && selectedVehicle && (
        <div className="qr-inspection-banner">
          <QrCode size={22} />
          <div>
            <strong>Inspeção iniciada pelo QR Code</strong>
            <span>
              Veículo: {selectedVehicle.identifier}
              {selectedVehicle.plate ? " · " + selectedVehicle.plate : ""}
            </span>
          </div>
        </div>
      )}

      {showForm && (
        <form className="panel inspection-form" onSubmit={submit}>
          <div className="inspection-form-heading">
            <div>
              <span className="section-kicker">NOVO CHECKLIST</span>
              <h2>Inspeção operacional</h2>
              <p className="muted">Após a finalização, o registro fica preservado e o relatório é gerado automaticamente.</p>
            </div>
          </div>

          <div className="traceability-note">
            <Clock3 size={20} />
            <div>
              <strong>Data e hora automáticas</strong>
              <span>O horário exato de finalização é registrado pelo servidor, com segundos, e exibido em horário de Brasília/Teresina (BRT).</span>
            </div>
          </div>

          <div className="form-grid">
            <label>Veículo / Equipamento
              <select
                value={vehicleId}
                disabled={qrMode}
                onChange={(e) => setVehicleId(e.target.value)}
              >
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.identifier} {v.plate ? "· " + v.plate : ""} {v.unit_name ? "· " + v.unit_name : ""}
                  </option>
                ))}
              </select>
              {qrMode && <small className="field-help">Veículo definido pelo QR Code.</small>}
            </label>

            <label>
              {meterType === "km" ? "Quilometragem atual (KM)" : "Horímetro atual"}
              <div className="input-icon-wrap">
                <Gauge size={18} />
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  required={meterRequired}
                  placeholder={meterRequired ? "Obrigatório" : "Informe quando aplicável"}
                  value={meterValue}
                  onChange={(e) => setMeterValue(e.target.value)}
                />
              </div>
              <small className="field-help">
                {meterRequired
                  ? "Obrigatório para veículos rodoviários."
                  : "Para empilhadeira, informe o horímetro quando disponível."}
              </small>
            </label>
          </div>

          <div className="checklist">
            {items.map((item, index) => (
              <article key={item.id} className="check-item">
                <div className="check-item-title">
                  <span className="check-number">{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <small>{item.section}</small>
                    <h3>{item.question}</h3>
                  </div>
                </div>
                <div className="answer-grid">
                  <select
                    required
                    value={answers[item.id]?.status || ""}
                    onChange={(e) => setAnswers({
                      ...answers,
                      [item.id]: { ...answers[item.id], status: e.target.value as AnswerStatus }
                    })}
                  >
                    <option value="">Selecione...</option>
                    <option value="conforme">Conforme</option>
                    <option value="ressalva">Ressalva</option>
                    <option value="nao_conforme">Não conforme</option>
                  </select>

                  <div className={"fixed-severity severity " + item.severity} title="Classificação definida pelo administrador">
                    {item.severity === "gravissima"
                      ? "Gravíssima"
                      : item.severity === "grave"
                        ? "Grave"
                        : item.severity === "moderada"
                          ? "Moderada"
                          : "Leve"}
                    <small>Definida pelo administrador</small>
                  </div>

                  <input
                    placeholder="Observação (opcional)"
                    value={answers[item.id]?.observation || ""}
                    onChange={(e) => setAnswers({
                      ...answers,
                      [item.id]: { ...answers[item.id], observation: e.target.value }
                    })}
                  />
                </div>
              </article>
            ))}
          </div>

          <label>Observações gerais
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          <div className="photo-area">
            {photoStorageReady === false && (
              <div className="alert warning storage-warning">
                Armazenamento de fotos ainda não configurado. Finalize a configuração do R2 antes de usar fotos nas inspeções.
              </div>
            )}

            <div className="row-between">
              <div>
                <b>Fotos</b>
                <p className="muted">Opcionais. No celular, a câmera traseira será solicitada.</p>
              </div>
              <CameraCapture
                onCapture={(photo) => setPhotos([...photos, photo])}
                disabled={photoStorageReady === false}
              />
            </div>
            <div className="photo-grid">
              {photos.map((photo, index) => (
                <div className="photo-preview" key={index}>
                  <img src={photo} alt={"Foto " + (index + 1)} />
                  <button type="button" onClick={() => setPhotos(photos.filter((_, i) => i !== index))}>×</button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <b>Assinatura do inspetor</b>
            <p className="muted">Opcional. A assinatura aparecerá no relatório.</p>
            <SignaturePad onChange={setSignature} />
          </div>

          <div className="finalization-warning">
            <strong>Registro final</strong>
            <span>Ao finalizar, o sistema grava o checklist, usuário, leitura informada e data/hora do servidor para fins de rastreabilidade.</span>
          </div>

          <button className="primary full finalize-inspection" type="submit" disabled={submitting}>
            {submitting ? "Finalizando inspeção..." : "Finalizar inspeção e gerar relatório"}
          </button>
        </form>
      )}

      <div className="panel unit-filter-bar inspection-unit-filter">
        <div>
          <span className="section-kicker">UNIDADE</span>
          <strong>Filtrar histórico de inspeções</strong>
        </div>
        <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
          <option value="">Todas as unidades</option>
          {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
        </select>
      </div>

      <div className="panel table-wrap inspection-history-table">
        <div className="table-panel-heading">
          <div>
            <span className="section-kicker">HISTÓRICO</span>
            <h2>Inspeções realizadas</h2>
          </div>
          <small>{filteredInspections.length} registro(s)</small>
        </div>

        <table>
          <thead>
            <tr>
              <th>Data e hora</th>
              <th>Veículo</th>
              <th>Unidade</th>
              <th>KM / Horímetro</th>
              <th>Inspetor</th>
              <th>Resultado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredInspections.map((i) => (
              <tr key={i.id}>
                <td>{formatBrt(i.created_at)}</td>
                <td>{i.vehicle_identifier}</td>
                <td>{i.unit_name || "Não definida"}</td>
                <td>
                  {i.meter_value != null
                    ? `${i.meter_value.toLocaleString("pt-BR")} ${i.meter_type === "horimetro" ? "h" : "km"}`
                    : "—"}
                </td>
                <td>{i.inspector_name}</td>
                <td>
                  <span className={"result " + i.result}>
                    {i.result === "apto" ? "APTO" : i.result === "apto_ressalva" ? "APTO COM RESSALVA" : "NÃO APTO"}
                  </span>
                </td>
                <td>
                  <div className="actions inspection-actions">
                    <Link className="secondary report-link" to={"/inspecoes/" + i.id + "/relatorio"}>
                      <FileText size={16} /> Relatório
                    </Link>
                    {isAdmin && (
                      <button
                        type="button"
                        className="danger-outline icon-action"
                        onClick={() => removeInspection(i)}
                      >
                        <Trash2 size={15} /> Excluir
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {!filteredInspections.length && (
              <tr><td colSpan={7} className="empty">Nenhuma inspeção registrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="inspection-history-mobile">
        <div className="table-panel-heading">
          <div>
            <span className="section-kicker">HISTÓRICO</span>
            <h2>Inspeções realizadas</h2>
          </div>
          <small>{filteredInspections.length} registro(s)</small>
        </div>

        {filteredInspections.map((i) => (
          <article className="inspection-mobile-card" key={"mobile-" + i.id}>
            <div className="inspection-mobile-top">
              <div>
                <span>Veículo / equipamento</span>
                <strong>{i.vehicle_identifier}</strong>
                <small>{i.unit_name || "Unidade não definida"}</small>
              </div>
              <span className={"result " + i.result}>
                {i.result === "apto" ? "APTO" : i.result === "apto_ressalva" ? "APTO COM RESSALVA" : "NÃO APTO"}
              </span>
            </div>

            <div className="inspection-mobile-grid">
              <div>
                <span>Data e hora</span>
                <strong>{formatBrt(i.created_at)}</strong>
              </div>
              <div>
                <span>KM / Horímetro</span>
                <strong>
                  {i.meter_value != null
                    ? `${i.meter_value.toLocaleString("pt-BR")} ${i.meter_type === "horimetro" ? "h" : "km"}`
                    : "—"}
                </strong>
              </div>
              <div className="full-mobile">
                <span>Inspetor</span>
                <strong>{i.inspector_name}</strong>
              </div>
            </div>

            <div className="inspection-mobile-actions">
              <Link className="secondary report-link mobile-report-button" to={"/inspecoes/" + i.id + "/relatorio"}>
                <FileText size={16} /> Abrir relatório
              </Link>
              {isAdmin && (
                <button
                  type="button"
                  className="danger-outline mobile-delete-inspection"
                  onClick={() => removeInspection(i)}
                >
                  <Trash2 size={15} /> Excluir teste
                </button>
              )}
            </div>
          </article>
        ))}

        {!inspections.length && <div className="panel empty">Nenhuma inspeção registrada.</div>}
      </div>
    </section>
  );
}
